/**
 * useIntersectionObserver - 高度なIntersection Observer用カスタムフック
 * 最適化されたlazy loading、仮想化、可視性検出に対応
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

interface IntersectionObserverConfig {
  /** 閾値（0-1の範囲、配列も可） */
  threshold?: number | number[];
  /** ルートマージン（CSS形式、例: "10px 20px 30px 40px") */
  rootMargin?: string;
  /** ルート要素（未指定時はviewport） */
  root?: Element | null;
  /** 一度だけ観測するか */
  triggerOnce?: boolean;
  /** 初期表示状態 */
  initialIsIntersecting?: boolean;
  /** 可視性変更時のコールバック */
  onVisibilityChange?: (isVisible: boolean, entry: IntersectionObserverEntry) => void;
}

interface UseIntersectionObserverResult {
  /** 要素のref */
  ref: React.RefObject<Element>;
  /** 現在の可視状態 */
  isIntersecting: boolean;
  /** IntersectionObserverEntry（最新） */
  entry: IntersectionObserverEntry | null;
  /** 可視性の割合（0-1） */
  intersectionRatio: number;
  /** 手動で再開始 */
  restart: () => void;
  /** 観測停止 */
  disconnect: () => void;
}

/**
 * 高度なIntersection Observer用カスタムフック
 */
export const useIntersectionObserver = ({
  threshold = 0.1,
  rootMargin = '50px',
  root = null,
  triggerOnce = false,
  initialIsIntersecting = false,
  onVisibilityChange
}: IntersectionObserverConfig = {}): UseIntersectionObserverResult => {
  
  const [isIntersecting, setIsIntersecting] = useState(initialIsIntersecting);
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  const [intersectionRatio, setIntersectionRatio] = useState(0);
  
  const elementRef = useRef<Element>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const hasTriggeredRef = useRef(false);

  // オブザーバーの設定メモ化
  const observerOptions = useMemo(() => ({
    threshold,
    rootMargin,
    root
  }), [threshold, rootMargin, root]);

  // エントリーハンドラー
  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const [intersectionEntry] = entries;
    
    if (!intersectionEntry) return;

    const { isIntersecting: currentlyIntersecting, intersectionRatio: currentRatio } = intersectionEntry;
    
    setEntry(intersectionEntry);
    setIntersectionRatio(currentRatio);
    
    // triggerOnceの場合、一度トリガーされたら状態を保持
    if (triggerOnce && hasTriggeredRef.current && !currentlyIntersecting) {
      return;
    }
    
    setIsIntersecting(currentlyIntersecting);
    
    // 可視性変更コールバック
    onVisibilityChange?.(currentlyIntersecting, intersectionEntry);
    
    // triggerOnceフラグ更新
    if (triggerOnce && currentlyIntersecting) {
      hasTriggeredRef.current = true;
    }
  }, [triggerOnce, onVisibilityChange]);

  // オブザーバー初期化
  const initializeObserver = useCallback(() => {
    // 既存のオブザーバーを破棄
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // 新しいオブザーバーを作成
    observerRef.current = new IntersectionObserver(handleIntersection, observerOptions);
    
    // 要素が存在する場合は観測開始
    if (elementRef.current) {
      observerRef.current.observe(elementRef.current);
    }
  }, [handleIntersection, observerOptions]);

  // 手動再開始
  const restart = useCallback(() => {
    hasTriggeredRef.current = false;
    setIsIntersecting(initialIsIntersecting);
    setEntry(null);
    setIntersectionRatio(0);
    initializeObserver();
  }, [initialIsIntersecting, initializeObserver]);

  // 観測停止
  const disconnect = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  // 初期化とクリーンアップ
  useEffect(() => {
    initializeObserver();
    
    return () => {
      disconnect();
    };
  }, [initializeObserver, disconnect]);

  // 要素変更時の再観測
  useEffect(() => {
    if (observerRef.current && elementRef.current) {
      observerRef.current.observe(elementRef.current);
    }
  }, []);

  return {
    ref: elementRef,
    isIntersecting,
    entry,
    intersectionRatio,
    restart,
    disconnect
  };
};

/**
 * より簡単なlazy loading用フック
 */
export const useLazyLoading = (options: {
  rootMargin?: string;
  threshold?: number;
  triggerOnce?: boolean;
} = {}) => {
  const {
    rootMargin = '100px',
    threshold = 0.01,
    triggerOnce = true
  } = options;

  return useIntersectionObserver({
    rootMargin,
    threshold,
    triggerOnce,
    initialIsIntersecting: false
  });
};

/**
 * 可視性追跡用フック（無限スクロール等）
 */
export const useVisibilityTracking = (
  onVisible: (entry: IntersectionObserverEntry) => void,
  options: {
    rootMargin?: string;
    threshold?: number;
  } = {}
) => {
  const {
    rootMargin = '200px',
    threshold = 0.1
  } = options;

  return useIntersectionObserver({
    rootMargin,
    threshold,
    triggerOnce: false,
    onVisibilityChange: (isVisible, entry) => {
      if (isVisible) {
        onVisible(entry);
      }
    }
  });
};

export default useIntersectionObserver;